// Modelo do Dashboard. Tudo aqui é função pura sobre o que a API devolveu — a
// tela só desenha, e é aqui que os testes moram.
//
// Fuso: o back já entrega os buckets do resumo em hora LOCAL da operação
// ("2026-08-18T10:00", ver CallSummary.perHour). Comparar esse rótulo com o
// relógio do navegador só é seguro porque as duas pontas falam do mesmo fuso —
// por isso o corte de "hoje" e "ontem" é feito por comparação de STRING de dia,
// nunca convertendo o rótulo de volta para Date (que reintroduziria fuso).
import type { CallSummary, DialerAgentAvailability, LeadStatusDef } from "./api.js";
import type { NamedDialerQueue } from "./api/queue-config.js";
import { buildCallKpis } from "./calls-kpis.js";

export interface HourBucket {
  hour: string;
  total: number;
  completed: number;
  // Ligações com tabulação de conversa. Opcional: back antigo não manda, e o
  // ritmo volta a contar `completed`.
  humanContacts?: number;
}

export interface PacePoint {
  hour: number;
  // null = hora que ainda não chegou. Zero seria mentira: diria "essa hora
  // aconteceu e ninguém ligou".
  today: number | null;
  yesterday: number | null;
}

export interface TodayPace {
  series: PacePoint[];
  today: number;
  // Atendidas de hoje até agora: contato humano confirmado pela tabulação, não
  // o canal atendido (caixa postal da operadora também atende). Vem do mesmo
  // bucket do total, então não custa uma segunda consulta.
  answeredToday: number;
  // Quanto ontem tinha somado até ESTA hora — o único comparável honesto com um
  // dia ainda em curso.
  yesterdayToNow: number;
  // Variação percentual contra ontem até agora. null quando ontem foi zero:
  // "+∞%" não informa nada.
  deltaPct: number | null;
}

// "2026-08-18T10:00" → "2026-08-18" e 10.
function splitBucket(hour: string): { day: string; index: number } {
  return { day: hour.slice(0, 10), index: Number(hour.slice(11, 13)) };
}

function localDay(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function todayPace(buckets: HourBucket[], now: Date): TodayPace {
  const today = localDay(now);
  const yesterday = localDay(new Date(now.getTime() - 24 * 60 * 60 * 1000));
  const currentHour = now.getHours();

  const todayByHour: number[] = Array.from({ length: 24 }, () => 0);
  const yesterdayByHour: number[] = Array.from({ length: 24 }, () => 0);
  const answeredByHour: number[] = Array.from({ length: 24 }, () => 0);
  for (const bucket of buckets) {
    const { day, index } = splitBucket(bucket.hour);
    if (Number.isNaN(index) || index < 0 || index > 23) continue;
    if (day === today) {
      todayByHour[index]! += bucket.total;
      answeredByHour[index]! += bucket.humanContacts ?? bucket.completed;
    } else if (day === yesterday) yesterdayByHour[index]! += bucket.total;
  }

  const series: PacePoint[] = Array.from({ length: 24 }, (_, hour) => ({
    hour,
    today: hour <= currentHour ? todayByHour[hour]! : null,
    // Ontem é cortado na hora atual de propósito: o dia de ontem inteiro ao lado
    // de um dia pela metade faria a operação parecer sempre atrasada.
    yesterday: hour <= currentHour ? yesterdayByHour[hour]! : null,
  }));

  const sum = (values: number[]) => values.slice(0, currentHour + 1).reduce((acc, v) => acc + v, 0);
  const todayTotal = sum(todayByHour);
  const yesterdayToNow = sum(yesterdayByHour);

  return {
    series,
    today: todayTotal,
    answeredToday: sum(answeredByHour),
    yesterdayToNow,
    deltaPct:
      yesterdayToNow > 0
        ? Math.round(((todayTotal - yesterdayToNow) / yesterdayToNow) * 100)
        : null,
  };
}

export interface IdleAgent {
  userId: string;
  name: string;
  seconds: number;
}

// Quem está de plantão, aceitando lead e SEM ligação — o vendedor que o gestor
// precisa cutucar. Em ligação não conta (está trabalhando) e offline também não
// (nem entrou). Do mais parado para o menos.
export function idleAgents(agents: DialerAgentAvailability[], now: Date): IdleAgent[] {
  const nowMs = now.getTime();
  return agents
    .filter((a) => a.status === "AVAILABLE" && !a.inCall && a.availableSinceAt)
    .map((a) => ({
      userId: a.userId,
      name: a.name,
      seconds: Math.max(0, Math.round((nowMs - Date.parse(a.availableSinceAt!)) / 1000)),
    }))
    .filter((a) => Number.isFinite(a.seconds))
    .sort((a, b) => b.seconds - a.seconds);
}

export interface QueueColumn {
  key: string;
  label: string;
  color: string;
  total: number;
}

export interface QueueColumns {
  columns: QueueColumn[];
  // O que sobrou fora das colunas exibidas — some da lista, não da soma.
  rest: number;
  total: number;
}

// Contagem da fila por status, na ordem do PESO (é o peso que decide quem o
// discador entrega primeiro, não o sortOrder da tela). Status fora do catálogo
// ATIVO não entra: lead com status assim não está na fila.
export function queueColumns(
  totals: { status: string; total: number }[],
  statuses: LeadStatusDef[],
  limit: number,
): QueueColumns {
  const active = new Map(statuses.filter((s) => s.active).map((s) => [s.key, s]));
  const ranked = totals
    .flatMap(({ status, total }) => {
      const def = active.get(status);
      return def
        ? [{ key: def.key, label: def.label, color: def.color, total, weight: def.queueWeight }]
        : [];
    })
    .sort((a, b) => b.weight - a.weight || a.label.localeCompare(b.label));

  const columns = ranked.slice(0, limit).map(({ weight: _weight, ...col }) => col);
  return {
    columns,
    rest: ranked.slice(limit).reduce((acc, c) => acc + c.total, 0),
    total: ranked.reduce((acc, c) => acc + c.total, 0),
  };
}

// Faixa do dia para a saudação do cabeçalho. Devolve a CHAVE, não o texto: a
// tradução mora no locale, e assim o corte de hora fica testável sem i18n.
export function greetingKey(now: Date): "morning" | "afternoon" | "evening" {
  const hour = now.getHours();
  if (hour < 12) return "morning";
  if (hour < 18) return "afternoon";
  return "evening";
}

export interface TeamPresence {
  total: number;
  inCall: number;
  available: number;
  offDuty: number;
}

// Presença do time em três baldes. "Fora" é tudo que não está em ligação nem
// disponível — offline e pausado juntos: para o gestor os dois significam a
// mesma coisa (não vai receber lead agora), e separá-los no cabeçalho só
// gastaria espaço.
export function teamPresence(agents: DialerAgentAvailability[]): TeamPresence {
  const inCall = agents.filter((a) => a.inCall).length;
  const available = agents.filter((a) => a.status === "AVAILABLE" && !a.inCall).length;
  return {
    total: agents.length,
    inCall,
    available,
    offDuty: agents.length - inCall - available,
  };
}

// Ordem do cluster de avatares: quem está em ligação primeiro, depois quem está
// disponível, e por último quem está fora. O cluster corta nos primeiros N, e
// esse corte tem que sobrar para quem está fora — não para quem está no ar.
export function presenceOrder(agents: DialerAgentAvailability[]): DialerAgentAvailability[] {
  const rank = (a: DialerAgentAvailability) => (a.inCall ? 0 : a.status === "AVAILABLE" ? 1 : 2);
  return [...agents].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
}

export interface QueueShare extends QueueColumn {
  // 0–100, fatia desta coluna no total da fila (inclusive o `rest`, que não
  // aparece como barra mas continua no denominador — senão as barras somariam
  // 100% escondendo que há lead fora das colunas exibidas).
  share: number;
}

export function queueShares(queue: QueueColumns): QueueShare[] {
  return queue.columns.map((column) => ({
    ...column,
    share: queue.total > 0 ? Math.round((column.total / queue.total) * 100) : 0,
  }));
}

// Matiz (0–359) do avatar de uma pessoa, derivada do nome. Serve só para
// DIFERENCIAR uma cara da outra num cluster — não carrega significado nenhum,
// então qualquer hash estável serve, desde que a mesma pessoa tenha sempre a
// mesma cor (um avatar que troca de cor a cada refetch vira ruído).
//
// Só a matiz varia: luminosidade e croma ficam fixos no componente. Sortear os
// três daria cores que somem no tema claro ou estouram no escuro.
export function avatarHue(name: string): number {
  const key = name.trim().toLowerCase();
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) % 360_000;
  }
  // O passo de 47 (primo com 360) espalha nomes parecidos — "Ana" e "Bia" caem
  // longe uma da outra em vez de virarem dois azuis quase iguais.
  return (hash * 47) % 360;
}

export type ActiveQueueLabel = { kind: "named"; name: string } | { kind: "none" };

// Qual fila nomeada o discador está usando. Três respostas, e a diferença
// importa: `null` é "o recurso não está em uso" (nenhuma fila cadastrada, nada
// a dizer na tela); `none` é "há filas montadas e nenhuma ligada" — aí o
// discador entrega por peso e o gestor precisa saber; `named` é o caso feliz.
//
// Id apontando para fila apagada cai em `none`: ativo por id que não resolve é,
// na prática, nenhuma fila ativa.
export function activeQueueLabel(
  config: { namedQueues?: NamedDialerQueue[]; activeQueueId?: string | null } | undefined,
): ActiveQueueLabel | null {
  const queues = config?.namedQueues ?? [];
  if (queues.length === 0) return null;
  const active = queues.find((queue) => queue.id === config?.activeQueueId);
  return active ? { kind: "named", name: active.name } : { kind: "none" };
}

export interface PeriodResult {
  total: number;
  answered: number;
  // Atendidas / total em %, inteiro. null sem chamada no período.
  ratePct: number | null;
  // Tempo falado médio das atendidas, em segundos. null sem atendida.
  avgSec: number | null;
}

// "Resultado do período": atendida é contato humano, com o mesmo critério do
// Relatório de Ligações (buildCallKpis). Contar o status COMPLETED inflava a
// taxa com caixa postal e aviso da operadora, que também atendem o canal — no
// preditivo da Connect eram 121 "atendidas" para 4 conversas.
export function periodResult(summary: CallSummary): PeriodResult {
  const kpis = buildCallKpis(summary);
  return {
    total: kpis.total,
    answered: kpis.answered.count,
    ratePct: kpis.total > 0 ? Math.round((kpis.answered.count / kpis.total) * 100) : null,
    avgSec: kpis.avgTalkSec,
  };
}
